'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, useTransition } from 'react';
import { TranslateSafeText } from '@/components/TranslateSafeText/TranslateSafeText';
import { Flex4XS, makeCentered } from '@/components/Flex';
import FormikTextInput from '@/components/core/formik/FormikTextInput';
import Typography from '@/components/core/Typography/Typography';
import { Field, Form, Formik } from 'formik';
import { useTranslations } from 'next-intl';
import * as Yup from 'yup';
import { Link, useRouter } from '@/i18n/routing';
import { logIn } from '@/actions/auth/login';
import useAmplitudeContext from '@/hooks/useAmplitudeContext';
import { AMPLITUDE_EVENTS, toAmplitudeUserID } from '@/context/AmplitudeContext';
import { getUser } from '@/actions/account/getUser';
import GoogleAuth, { postLoginRedirect } from '../../_components/GoogleAuth';
import { useGoogleAuthEnabled } from '@/context/GoogleAuthContext';
import { Frame, GreyTypography, PageWrapper2VerticalSections, StyledButton, StyledMainFrame, StyledSecondaryTitle, Title } from '@/app/[locale]/(welcome)/auth/_styled';
import FormikCheckbox from '@/components/core/formik/FormikCheckbox';
import { MoonOneLogo, NeedHelpBlock } from '@/app/[locale]/(welcome)/_components';
import { Link as TypographyLink } from '@/components/Link';
import { FlexCenter } from '@/components/Helpers';
import { EMAIL_REGEX } from '@/utils/regex';
import { FieldsBlock, ForgotPasswordButton, GoogleBlock, Line, OrBlock, StyledFlexSpaceBetween, StyledIcon } from '@/app/[locale]/(welcome)/auth/(with_banner)/login/_styled';
import AlertWithIcon from '@/components/core/Alert/AlertWithIcon';
import capitalize from '@/utils/capitalize';
import * as amplitude from '@amplitude/analytics-browser';
import { formatTitle } from '@/utils/formatTitle';
import { HideBelow, HideAbove, device } from '@/components/Media';
import styled from 'styled-components';
import { useClientOnly } from '@/hooks/useClientOnly';
const CenteredFlex4XS = makeCentered(Flex4XS, {
  x: true,
  y: true
});
const SignUpWrapper = styled(CenteredFlex4XS)`
    @media screen and ${device.laptopS} {
        flex-direction: column;
        align-items: center;
    }
`;
const validationSchema = t => Yup.object({
  email: Yup.string().email(t('validation.email')).required(t('validation.enterEmail')).matches(EMAIL_REGEX, t('validation.email')),
  password: Yup.string().required(t('validation.enterPassword'))
});
const initialFormValues = {
  email: '',
  password: '',
  remember_me: false
};
const Alerts = (t, error: string): React.ReactNode => {
  switch (error) {
    case 'user_is_not_active':
      return <AlertWithIcon severity='error'><TranslateSafeText>{t.rich('auth.deactivatedAccount', {
            br: () => <br />
          })}
                                                   </TranslateSafeText>
                   </AlertWithIcon>;
    case 'user_without_password':
      return <AlertWithIcon severity='error'><TranslateSafeText>{t.rich('auth.userWithoutPassword', {
            br: () => <br />
          })}
                                                   </TranslateSafeText>
                   </AlertWithIcon>;
    default:
      return <AlertWithIcon severity='error'>
                <TranslateSafeText>{t.rich('auth.loginIncorrectCredentials', {
            br: () => <br />
          })}
                </TranslateSafeText>
                   </AlertWithIcon>;
  }
};
function LoginPage() {
  const $ = _c(37);
  const t = useTranslations();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [, startTransition] = useTransition();
  const {
    trackEvent,
    setUserID,
    identify,
    setOptOut
  } = useAmplitudeContext();
  const hasMounted = useClientOnly();
  const googleAuthEnabled = useGoogleAuthEnabled();
  let t0;
  if ($[0] !== identify || $[1] !== router || $[2] !== setOptOut || $[3] !== setUserID || $[4] !== startTransition || $[5] !== trackEvent) {
    t0 = (values, t1) => {
      const {
        setSubmitting
      } = t1;
      startTransition(async () => {
        setLoginError("");
        const respError = await logIn(values.email, values.password, values.remember_me).catch(console.error);
        if (respError) {
          setOptOut(false);
          trackEvent(AMPLITUDE_EVENTS.SIGN_IN_ERROR);
          setLoginError(respError.code);
          setOptOut(true);
        } else {
          const user = await getUser();
          if (user) {
            setUserID(toAmplitudeUserID(user.id));
            setOptOut(false);
            trackEvent(AMPLITUDE_EVENTS.SIGN_IN, {
              role: user?.roles[0]?.name
            });
            const identifyEvent = new amplitude.Identify();
            identifyEvent.set("email", user.email);
            identify(identifyEvent);
          }
          postLoginRedirect(router, user);
        }
        setSubmitting(false);
      });
    };
    $[0] = identify;
    $[1] = router;
    $[2] = setOptOut;
    $[3] = setUserID;
    $[4] = startTransition;
    $[5] = trackEvent;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const handleSubmit = t0;
  let t1;
  if ($[7] !== t) {
    t1 = () => {
      document.title = formatTitle([t("meta.titles.login"), t("meta.titles.moonOne")]);
    };
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useEffect(t1, t2);
  if (!hasMounted) {
    return null;
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HideAbove md={true}><MoonOneLogo /></HideAbove>;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t) {
    t4 = t("auth.welcomeBack");
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <HideBelow md={true}><Title variant="uiType" size="3xl" modifier="S">{t4}</Title></HideBelow>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t("auth.welcomeBack");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <HideAbove md={true}><Title variant="uiType" size="xl" modifier="S">{t6}</Title></HideAbove>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = validationSchema(t);
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== googleAuthEnabled || $[22] !== loginError || $[23] !== showPassword || $[24] !== t) {
    t9 = t10 => {
      const {
        isSubmitting
      } = t10;
      return <Form noValidate={true} method="post"><Frame><HideBelow md={true}><StyledSecondaryTitle><GreyTypography variant="uiType" size="md" modifier="S">{t("auth.loginHint")}</GreyTypography></StyledSecondaryTitle></HideBelow><FieldsBlock><Field data-testid="login-email-input" component={FormikTextInput} type="email" name="email" placeholder={t("auth.emailPlaceholder")} autoComplete="email" withIncreasedHorizontalPadding={true} /><Field data-testid="login-password-input" component={FormikTextInput} name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder={t("auth.passwordPlaceholder")} withIncreasedHorizontalPadding={true} endAdornment={showPassword ? <StyledIcon name="eye-slash-light" onClick={() => setShowPassword(false)} /> : <StyledIcon name="eye-light" onClick={() => setShowPassword(true)} />} /><StyledFlexSpaceBetween><Field component={FormikCheckbox} name="remember_me" label={t("auth.rememberMe")} inverted={true} /><Link href="/auth/forgot-password"><ForgotPasswordButton variant="ghost" size="xs" type="button" innerText={<Typography variant="uiType" modifier="S" size="sm">{t("auth.forgotPassword")}</Typography>} /></Link></StyledFlexSpaceBetween></FieldsBlock><FieldsBlock><StyledButton data-testid="login-submit-button" type="submit" variant="filled" size="sm" innerText={t("auth.signIn")} disabled={isSubmitting} />{googleAuthEnabled && <OrBlock><Line />{capitalize(t("common.or"))}<Line /></OrBlock>}<GoogleBlock>{googleAuthEnabled && <GoogleAuth variant="SignIn" />}{loginError && Alerts(t, loginError)}<FlexCenter><SignUpWrapper><div><GreyTypography variant="uiType" size="sm" modifier="R">{t("auth.dontHaveAnAccount")} </GreyTypography></div><TypographyLink data-testid="login-signup-link" href="/auth/signup" variant="uiType" size="sm" modifier="S">{t("auth.signUp")}</TypographyLink></SignUpWrapper></FlexCenter></GoogleBlock></FieldsBlock></Frame></Form>;
    };
    $[21] = googleAuthEnabled;
    $[22] = loginError;
    $[23] = showPassword;
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== handleSubmit || $[27] !== t8 || $[28] !== t9) {
    t10 = <Formik initialValues={initialFormValues} onSubmit={handleSubmit} validationSchema={t8}>{t9}</Formik>;
    $[26] = handleSubmit;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t5 || $[32] !== t7) {
    t11 = <StyledMainFrame>{t5}{t7}{t10}</StyledMainFrame>;
    $[30] = t10;
    $[31] = t5;
    $[32] = t7;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <NeedHelpBlock />;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t11) {
    t13 = <PageWrapper2VerticalSections>{t3}{t11}{t12}</PageWrapper2VerticalSections>;
    $[35] = t11;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
export default LoginPage;